import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Flex,
  Heading,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { difference, isEmpty } from 'lodash';
import union from 'lodash/union';
import { ChangeEvent, useCallback } from 'react';
import { ArrowLeftIcon } from 'ui-components/dist/ArrowLeftIcon';
import { Button } from 'ui-components/dist/Button';
import { FilterIcon } from 'ui-components/dist/FilterIcon';
import { Checkbox } from '~/components/Checkbox2';
import {
  CheckboxGroup,
  ICheckboxGroupOnChange,
} from '~/components/CheckboxGroup';
import IconButton from '~/components/IconButton';
import {
  INSTAGRAM_CONTENT_TYPES,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORMS_NAMES,
} from '~/constants';
import { getPlatformIcon } from '~/core/utils';
import {
  CONTENT_SEARCH_ORDER_BY,
  CONTENT_SEARCH_PARAMS,
} from '~/hooks/useContentSearch';
import { useContentSearchFilterContext } from '~/screens/SearchContent/ContentSearchFilterContext';

import {
  CONTENT_SEARCH_IG_CHANNELS,
  CONTENT_SEARCH_PLATFORM_CHANNEL,
} from '../ContentSearchFilter.constants';
import { IContentSearchComponentProps } from '../ContentSearchFilter.types';

export const ContentSearchFilterPlatform: React.FC<
  IContentSearchComponentProps
> = ({
  filters: { platform: platforms = [] },
  onChange,
  order,
  trackId,
  isDisabled,
}) => {
  const { isOpen, onClose, onToggle } = useDisclosure();

  const handleClear = useCallback(() => {
    onChange({
      [CONTENT_SEARCH_PARAMS.channel]: [],
      [CONTENT_SEARCH_PARAMS.platform]: [],
    });
  }, [onChange]);

  return (
    <Box order={order} pointerEvents={isDisabled ? 'none' : 'auto'}>
      <Button
        variant="tertiary"
        onClick={onToggle}
        leftIcon={<FilterIcon boxSize={4} />}
        size="md"
        track-id={trackId}
      >
        Platform {Boolean(platforms.length) && `(${platforms.length})`}
      </Button>

      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement="right"
        blockScrollOnMount={false}
      >
        <DrawerContent track-id="filterbar-platform-drawer">
          <DrawerHeader>
            <Flex flexWrap="wrap">
              <IconButton
                aria-label="Close platform filter"
                icon={<ArrowLeftIcon boxSize={4} />}
                marginRight={2}
                onClick={onClose}
              />

              <Heading
                display="inline-flex"
                alignItems="center"
                as="h5"
                fontSize="2xl"
              >
                Filter by:
              </Heading>

              <Button
                variant="tertiary"
                onClick={handleClear}
                size="sm"
                sx={{
                  marginLeft: 'auto',
                }}
              >
                Clear
              </Button>
            </Flex>
          </DrawerHeader>

          <DrawerBody>
            <ContentSearchFilterPlatformList trackId={trackId} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export function ContentSearchFilterPlatformList({
  trackId,
}: {
  trackId?: string;
}) {
  const { filters, onChange } = useContentSearchFilterContext();
  const { channel: channels = [], platform: platforms = [] } = filters;

  const areAllIgChannelsSelected = useCallback(
    (optionsChecked: INSTAGRAM_CONTENT_TYPES[]) =>
      !difference(CONTENT_SEARCH_IG_CHANNELS, optionsChecked).length ||
      isEmpty(optionsChecked),
    [],
  );

  const handleCheckboxGroupChange = useCallback(
    ({ optionsChecked, isChecked, value }: ICheckboxGroupOnChange) => {
      let updatedPlatforms: SOCIAL_PLATFORMS[];
      const optionsCheckedValues = optionsChecked.map(
        (option) => option.value,
      ) as INSTAGRAM_CONTENT_TYPES[];

      const platformChannel =
        CONTENT_SEARCH_PLATFORM_CHANNEL[value as SOCIAL_PLATFORMS]?.map(
          ({ value }) => value,
        ) ?? [];

      const updatedChannels = platformChannel.reduce(
        (acc: string[], channel) => {
          const isChecked = optionsChecked.find(
            ({ value }) => value === channel,
          );
          if (isChecked) {
            acc.push(channel);
          }
          return acc;
        },
        (channels || []).filter(
          (channel: INSTAGRAM_CONTENT_TYPES) =>
            !platformChannel.includes(channel),
        ),
      ) as INSTAGRAM_CONTENT_TYPES[];

      if (areAllIgChannelsSelected(optionsCheckedValues)) {
        updatedPlatforms = (
          isChecked
            ? union(platforms, [value])
            : platforms.filter((platform) => platform !== value)
        ) as SOCIAL_PLATFORMS[];
      } else {
        updatedPlatforms = [SOCIAL_PLATFORMS.instagram];
      }

      onChange({
        [CONTENT_SEARCH_PARAMS.channel]: updatedChannels,
        [CONTENT_SEARCH_PARAMS.platform]: updatedPlatforms,
      });
    },
    [channels, onChange, platforms, areAllIgChannelsSelected],
  );

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const updatedPlatforms = event.target.checked
        ? union(platforms, [event.target.value])
        : platforms.filter((platform) => platform !== event.target.value);

      onChange({
        [CONTENT_SEARCH_PARAMS.platform]: updatedPlatforms,
        [CONTENT_SEARCH_PARAMS.sortBy]: CONTENT_SEARCH_ORDER_BY.publishedAt,
      });
    },
    [onChange, platforms],
  );

  return (
    <VStack alignItems="flex-start" justifyContent="flex-start">
      {Object.values(SOCIAL_PLATFORMS).map((platform) => {
        const options = CONTENT_SEARCH_PLATFORM_CHANNEL[platform];
        const Icon = getPlatformIcon(platform as SOCIAL_PLATFORMS);
        const title = SOCIAL_PLATFORMS_NAMES[platform];
        const isPlatformDisabled =
          !areAllIgChannelsSelected(channels as INSTAGRAM_CONTENT_TYPES[]) &&
          platform !== SOCIAL_PLATFORMS.instagram;

        if (options) {
          return (
            <CheckboxGroup
              checked={channels || []}
              enableTitleInOptions={false}
              friendlyValue={SOCIAL_PLATFORMS_NAMES[platform]}
              icon={<Icon boxSize={4} marginRight={2} />}
              key={platform}
              onChange={handleCheckboxGroupChange}
              options={options}
              value={platform}
              trackId={trackId + '-' + platform}
            />
          );
        }

        return (
          <Checkbox
            fontWeight={500}
            isChecked={platforms.includes(platform)}
            key={platform}
            leftIcon={<Icon boxSize={4} marginRight={2} />}
            onChange={handleCheckboxChange}
            value={platform}
            variant="right"
            trackId={trackId + '-' + platform}
            isDisabled={isPlatformDisabled}
          >
            {title}
          </Checkbox>
        );
      })}
    </VStack>
  );
}
